import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ChevronDown, MessageCircle } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { api } from "@/lib/api";
import { useApply } from "@/context/ApplyContext";
import { IMAGES } from "@/data/content";
import { useSEO } from "@/hooks/useSite";

export default function Faqs() {
  const [faqs, setFaqs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const apply = useApply();
  useSEO({
    title: "FAQs | IAUE School of Basic Studies — JUPEB & IJMB",
    description: "Frequently asked questions about JUPEB, IJMB, eligibility, Direct Entry, fees and applying at the School of Basic Studies, IAUE Port Harcourt.",
  });

  useEffect(() => { api.get("/faqs").then(({ data }) => setFaqs(data)).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return faqs;
    return faqs.filter((f) => f.question.toLowerCase().includes(t) || f.answer.toLowerCase().includes(t));
  }, [q, faqs]);

  return (
    <>
      <PageHero overline="Support" title="Frequently Asked Questions"
        subtitle="Answers to the most common questions from students and parents." crumb="FAQs" image={IMAGES.campus} />

      <section className="py-16 sm:py-24 bg-white">
        <div className="container-x max-w-3xl">
          <div className="relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search questions…"
              className="w-full rounded-sm border border-slate-300 bg-[#F7F5EF] py-4 pl-12 pr-4 text-slate-900 outline-none focus:border-[#0A1E4E] focus:ring-2 focus:ring-[#0A1E4E]/15"
              data-testid="faq-search-input"
            />
          </div>

          <div className="mt-8 divide-y divide-slate-200 border-y border-slate-200">
            {filtered.map((f, i) => (
              <div key={f.id}>
                <button className="flex w-full items-start justify-between gap-4 py-5 text-left" onClick={() => setOpen(open === i ? null : i)} data-testid={`faq-item-${i}`}>
                  <span className="font-serif text-lg font-bold text-[#0A1E4E]">{f.question}</span>
                  <ChevronDown size={20} className={`mt-1 shrink-0 text-[#D4AF37] transition-transform ${open === i ? "rotate-180" : ""}`} />
                </button>
                <AnimatePresence initial={false}>
                  {open === i && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                      <p className="pb-5 text-slate-600 leading-relaxed">{f.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
            {filtered.length === 0 && <p className="py-10 text-center text-slate-500">No questions match your search.</p>}
          </div>

          <div className="mt-10 rounded-sm bg-[#0A1E4E] p-7 text-center text-white">
            <h3 className="font-serif text-2xl font-bold">Still have a question?</h3>
            <p className="mt-2 text-blue-100/80">Our admissions officers are happy to help.</p>
            <button className="btn-gold mt-5" onClick={() => apply?.openApply()} data-testid="faq-talk-officer">
              <MessageCircle size={16} /> Talk to an Admissions Officer
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
